import React from 'react'
import AppUI from './knack'
import axios from 'axios'
import { ObjectState, TOPID, FetchOptions } from '../../utils/appdata'
import { assembleText } from 'yatte'
import DataBuffer from '../../utils/DataBuffer'
import ReactGA from 'react-ga'
import { toast } from 'react-toastify'
import queryString from 'query-string'
import { startTimer, finishTimer } from '../../utils/timers'
import Loading from './loading'

const USERDATA = TOPID

const getDefinitions = async (adapter, application) => {

  const retry = (version) => new Promise((resolve => {
    setTimeout(async () => {
      // console.log(`retrying request for version ${version} after delay (timer=${timer})`)
      const response = await axios.get(
        `/api/collections/${adapter}/apps/${application}/def${window.location.search}`,
        {
          headers: {
            version,
          },
        })
      // console.log(`got response to retried request with status ${response.status} (timer=${timer})`)
      if (response.status === 202) {
        const version = response.data
        const nestedResult = await retry(version)
        // console.log(`resolving with nested result (timer=${timer})`)
        return resolve(nestedResult)
      }
      if (response.status !== 200) {
        // console.log('throwing error with statusText')
        throw Error(response.statusText)
      }
      // console.log(`resolving with response data (timer=${timer})`)
      resolve(response.data)
    }, 1000)
    // console.log(`setTimeout ${timer} started`)
  }))
  // console.log('making initial request for definition')
  let response = await axios.get(
    `/api/collections/${adapter}/apps/${application}/def${window.location.search}`)
  // console.log(`initial request returned with status ${response.status}`)
  if (response.status === 202) {
    const version = response.data
    return await retry(version)
  }

  if (response.status !== 200) {
    throw Error(response.statusText)
  }

  return response.data
}

/**
 * AppContainer is a container component that manages the top-level state of an app (knack).
 * It fetches the app definition from the server and initializes the app's runtime environment:
 * the tables (static data) it needs, as well as the runtime helper objects for math, text, date and finance functions.
 */
// export default function AppContainer (props) {
export default class AppContainer extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      appDef: null,
      dataState: null,
      error: false,
      disableComplete: false,
      sessionId: null,
      isNew: false,
      blockRecord: false,
    }
    this.buffer = new DataBuffer()

    // binding for event handlers
    this.handleRunQuery = this.handleRunQuery.bind(this)
    this.handleAnyDataChanged = this.handleAnyDataChanged.bind(this)
    this.handleFinish = this.handleFinish.bind(this)
    this.handleSaveAndExit = this.handleSaveAndExit.bind(this)
    this.onContinue = this.onContinue.bind(this)
  }

  async componentDidMount () {
    const props = this.props
    const {
      match: { params },
      location,
    } = props

    const {
      adapter,
      application,
    } = params

    const qsParams = queryString.parse(location.search)
    let qsCompleteURL = qsParams &&
      (qsParams.return_complete || qsParams.return_url)
    let qsExitURL = qsParams &&
      (qsParams.return_incomplete || qsParams.return_url)
    const qsKey = qsParams && qsParams.key
    // later?: const qsInitLink = qsParams && qsParams.initlink
    let hideNav = false
    if (qsParams && ('hn' in qsParams)) {
      const hn = typeof qsParams.hn === 'string'
        ? qsParams.hn.toLowerCase()
        : ''
      if (hn === '' || hn === 'true' || hn === 'yes' || hn === '1') {
        hideNav = true
      }
    }

    // the location prop is set by React Router's Link element. When the user clicks the 'Review or Update' button
    // on a particular record, that record's ID and data are stored in the state of the location.
    //    see client/src/views/adapter/detail.js::228
    let id = params.record_id
    // Google Analytics
    ReactGA.event({
      category: window.tenancy + '_' + adapter,
      action: location.state ? 'Review or Update' : 'Run new application',
      label: application,
    })
    // clear yatte's expression evaluation cache to avoid stale definitions
    // (manifests in stale table definitions when used in option expressions)
    ObjectState.clearCaches()
    try {
      startTimer()
      const appDef = await getDefinitions(adapter, application)
      finishTimer('getDefinitions')
      const sessionFromURL = qsParams && qsParams.sessionId
      const sessionId = sessionFromURL || appDef.sessionId || null
      window.appInfo = {
        label: appDef.label || '',
        name: appDef.name || '',
        key: appDef.ksvid || '',
        env: appDef.envVersion || '',
      }
      if (appDef.extDependence) {
        const back_uri = window.location.href + '?sessionId=' + sessionId
        const dependence = appDef.extDependence[0]
        if (dependence) {
          if (dependence.error) {
            toast.warn(`${dependence.provider} : ${dependence.error}`, {
              position: toast.POSITION.TOP_CENTER,
              autoClose: 5000,
            })
            return
          }
          if (dependence.type === 'redirect') {
            const state = {
              tenancy: window.tenancy,
              back_uri,
              authorization: axios.defaults.headers.common['Authorization'],
            }
            const uri = new URL(dependence.uri)
            uri.searchParams.set('state', JSON.stringify(state))
            window.location.href = uri.toString()
          }
        }
      }
      // if someone passes key= on query string, that means look up an existing record with that key,
      // or create a new record with that key if it doesn't find an existing one
      let url = `/api/collections/${adapter}/apps/${application}/load/${id ||
      ''}${window.location.search}`
      if (!id && qsKey) {
        let keyName = 'id$'
        const typeDef = appDef.types.find(t => t.name === appDef.mainType)
        if (typeDef &&
          ((typeDef.keyType && typeDef.key) || typeDef.keyType === 'none')) {
          keyName = typeDef.key
        }
        url = `/api/collections/${adapter}/apps/${application}/load-key/${keyName}/${qsKey}`
      }
      const response = await axios.get(url)
      if (response.status !== 200) {
        throw Error(response.statusText)
      }
      const responseData = response.data
      const dataState = ObjectState.initializeRuntime(appDef, responseData.id,
        Array.isArray(responseData.data), this.handleRunQuery)
      if (responseData.id) {
        if (!id) {
          window.history.replaceState(null, '',
            `/${params.tenancy}/${adapter}/${responseData.id}/${application}`)
        }
        if (id && responseData.id !== id) {
          throw Error('Unexpected: id change when loading data')
        }
        this.loadData(dataState, responseData.data, responseData.id,
          responseData.links)
      } else {
        console.log('Warning: no record ID')
        // this.loadData(dataState, null)
      }
      // register to be notified of data changes
      dataState.register(this.handleAnyDataChanged)
      // manage stuff no longer on the query string so it's not lost if user refreshes page
      const appSessKey = `${responseData.id}/${application}/`
      if (qsCompleteURL) {
        localStorage.setItem(appSessKey + 'completeUrl', qsCompleteURL)
      } else {
        qsCompleteURL = localStorage.getItem(appSessKey + 'completeUrl')
      }
      if (qsExitURL) {
        localStorage.setItem(appSessKey + 'exitUrl', qsExitURL)
      } else {
        qsExitURL = localStorage.getItem(appSessKey + 'exitUrl')
      }
      if (hideNav) {
        localStorage.setItem(appSessKey + 'hideNav', 'true')
      } else {
        hideNav = (localStorage.getItem(appSessKey + 'hideNav') === 'true')
      }
      // update state to cause re-render
      this.setState({
        appDef,
        sessionId,
        dataState,
        qsCompleteURL,
        qsExitURL,
        hideNav,
        isNew: !id,
      })
      if (appDef && !appDef.extAccess) { // apps that can be accessed externally have no query definitions!
        // initiate async auto-refresh of previously-selected queries, if any
        await dataState.updateQueryLinks()
      }
      this.setState({ dataState }) // one more manual bump after queries updated
    } catch (e) {
      this.setState({ error: true })
      if (e.name === 'RecursionError') {
        console.error(e.name + ': ' + e.message,
          e.frames ? e.frames.join('\n') : undefined)
      } else {
        console.error(e)
        throw e
      }
    }
  }

  /* This callback is invoked when a query has been encountered DURING EVALUATION and the app runtime needs data. */
  async handleRunQuery (queryDef, args) {
    if (this.state.dataState) {
      // request all the fields this app might possibly need or use:
      const allFields = this.state.dataState.getVarNamesAll(queryDef.typeName,
        queryDef.provider === 'Knackly')
      return FetchOptions(queryDef, allFields, args, this.state.dataState, 0,
        '')
    }
  }

  loadData (dataState, pojo, recordId = undefined, links = undefined) {
    if (pojo || recordId) {
      const catalogName = this.props.match.params.adapter
      dataState.initMainContextMutable(pojo, recordId, catalogName, links,
        window.tenancy)
      this.postData(dataState, true, true)
    }
  }

  shouldComponentUpdate (nextProps, nextState) {
    if (this.state !== nextState || this.props !== nextProps) { // right now nothing ever changes in the state?
      return true
    }
    return false
  }

  handleAnyDataChanged (newObj, forceRefresh = false) {
    // NOTE: newObj (passed in as parameter) is a yObject representing the entirety of the new data state.
    // so here we refresh the entire data state to reflect changes to that yObject, and update the component state
    // (which in turn will trigger a re-render that gets the new data)
    // NOTE 2: if newObj is not passed in, we assume the dataState has already been updated... in that case,
    // forceRefresh can still be used to cause the state to be "jogged" thus initiating a re-render.
    // console.log(`handleAnyDataChanged called ${newObj ? 'WITH' : 'WITHOUT'} new object${forceRefresh ? ' (FORCED)' : ''}`)
    let mod = false
    const dataState = this.state.dataState
    if (newObj || forceRefresh) {
      if (!newObj) {
        newObj = { [USERDATA]: dataState.scope.value[USERDATA] }
      }
      if (newObj[USERDATA] !== dataState.scope.value[USERDATA] ||
        forceRefresh) {
        mod = true
        dataState.refreshUserData(newObj[USERDATA])
        this.buffer.set(dataState,
          snapshot => this.postData(snapshot, false, true))
      }
    }
    if (mod || forceRefresh) {
      this.startOrUpdateTimer(() => {
        this.setState({ blockRecord: true })
      }, 600000)
      // by passing an always-new object here (with a dataState property),
      // the state itself is always updated (thus always triggering a re-render)
      // even if the value of the dataState property is not updated.
      this.setState({ dataState, isNew: false })
    }
  }

  async handleSaveAndExit (e) {
    await this.handleFinish(e, true)
  }

  startOrUpdateTimer (updateFunction, delay) {
    if (this.timer) {
      clearTimeout(this.timer)
    }
    this.timer = setTimeout(updateFunction, delay)
  }

  async handleFinish (e, isIncomplete = false, isSaveData = true) {
    e.preventDefault()
    const props = this.props
    const {
      dataState,
      appDef,
      qsCompleteURL,
      qsExitURL,
      hideNav,
    } = this.state
    const pendingData = this.buffer.reset(true) // cancel pending save (if any)
    const context = pendingData || dataState
    const {
      match: {
        params: {
          adapter,
          application,
        },
      },
      history,
    } = props
    const isExternal = props.user.isExternal
    this.setState({ disableComplete: true })
    if (isSaveData) {
      await this.postData(context, true, isIncomplete)
    }
    ReactGA.event({
      category: window.tenancy + '_' + adapter,
      action: isIncomplete ? 'Save and Exit' : 'Complete',
      label: application,
    })
    if (qsCompleteURL || qsExitURL || hideNav) {
      // attempt to clean up local storage keys
      const appSessKey = `${context.recordId}/${application}/`
      localStorage.removeItem(appSessKey + 'completeUrl')
      localStorage.removeItem(appSessKey + 'exitUrl')
      localStorage.removeItem(appSessKey + 'hideNav')
    }
    if (isExternal) {
      let instruct
      if (isIncomplete) {
        // external user clicked "Save and Exit"
        const appExitURL = appDef.extUseURLs && appDef.extExitURL
        if (HasExitUrl(appExitURL, qsExitURL, context)) {
          return // HasExitUrl already replaced window.location!
        } // else
        const link = window.location.origin + window.location.pathname +
          '?external'
        instruct = `## Thank You

Your data has been saved. You may return at any time to continue and complete your work
by following the link you used to get here, or by bookmarking the following link:

 [${link}](${link})
`
        history.push('/' + window.tenancy + '/external', { instruct })
      } else {
        // external user is completing app
        const appCompleteURL = appDef.extUseURLs && appDef.extCompletionURL
        if (HasExitUrl(appCompleteURL, qsCompleteURL, context)) {
          return // HasExitUrl already replaced window.location!
        } // else
        if (appDef.extInstruct) {
          instruct = AssembleTextTemplate(appDef.extInstruct, context)
        } else {
          instruct = '## Thank You\n\nYour answers have been securely stored. Thank you for completing this app.'
        }
      }
      history.push('/' + window.tenancy + '/external', {
        instruct,
        showDocs: appDef.showDocs,
        collectionName: adapter,
        id: context.recordId,
        appName: application,
      })
    } else {
      // internal user is finishing or saving
      if (isIncomplete && qsExitURL) {
        window.location.replace(qsExitURL)
      } else if (!isIncomplete && qsCompleteURL) {
        window.location.replace(qsCompleteURL)
      } else {
        history.push('/' + window.tenancy + '/' + adapter)
      }
      // this.setState({ disableComplete: false })
    }
  }

  async postData (dataState, exitApp, suppressFiles) {
    const isExternal = this.props.user.isExternal
    const {
      name: appName,
      match: {
        params: {
          adapter,
          application,
        },
      },
    } = this.props
    window.localStorage.removeItem(appName)
    // const timestamp = format(Date.now(), 'T').slice(-6)
    try {
      const {
        links,
        values,
        recordId,
        appKey,
        _updating,
      } = dataState.getMainContextSavables()
      if (!_updating) {
        let url = `/api/collections/${adapter}/apps/${application}`
        if (!exitApp) {
          url += `/save/${recordId}`
        }
        // console.log('auto-save start ' + timestamp)
        await axios.post(
          url + window.location.search,
          {
            isWithOutFile: suppressFiles,
            isExternal,
            values,
            links,
            sessionId: this.state.sessionId,
            id: recordId,
          },
          {
            headers: { 'knackly-appkey': appKey },
          },
        )
        // console.log('auto-save finish ' + timestamp)
      }
    } catch (e) {
      console.error('auto-save failed', e) // timestamp
    }
  }

  onContinue () {
    this.handleAnyDataChanged(undefined, true)
    this.setState({ blockRecord: false })
  }

  render () {
    const props = this.props
    const {
      appDef,
      dataState,
      disableComplete,
      isNew,
      hideNav,
      blockRecord,
    } = this.state
    const isReady = appDef && (dataState instanceof ObjectState)
    if (this.state.error) {
      return null
    }
    return isReady ? (
      <AppUI name={appDef.name}
             isNew={isNew}
             linkedQuery={appDef.linkedQuery}
             autoLink={appDef.autoLink}
             selectFrom={appDef.selectFrom}
             autoSelect={appDef.autoSelect}
             selectOnce={appDef.selectOnce}
             disableComplete={disableComplete}
             hideNav={hideNav}
             context={dataState}
             finish={this.handleFinish}
             blockRecord={blockRecord}
             onContinue={this.onContinue}
             saveAndExit={this.handleSaveAndExit}
             {...props}
      />
    ) : (
      <Loading stage={0}/>
    )
  }
}

function AssembleTextTemplate (template, dataState) {
  const childState = dataState.getChildContext(USERDATA)
  const result = assembleText(template, childState.scope)
  return result && result.valueOf()
}

function HasExitUrl (appDefUrl, queryStrUrl, context) {
  if (appDefUrl || queryStrUrl) {
    let exitUrl
    if (appDefUrl) {
      if (appDefUrl.includes('{[') && appDefUrl.includes(']}')) {
        exitUrl = AssembleTextTemplate(appDefUrl, context)
      } else {
        exitUrl = appDefUrl
      }
      exitUrl = ValidateReturnUrl(exitUrl)
    } else {
      exitUrl = ValidateReturnUrl(queryStrUrl)
    }
    if (exitUrl) {
      window.location.replace(exitUrl)
      return true
    }
  }
}

function ValidateReturnUrl (returnUrl) {
  // Parse and re-serialize the return URL to ensure it is syntactically valid
  try {
    const parsed = queryString.parseUrl(returnUrl,
      { parseFragmentIdentifier: true })
    // LS: at first I thought we would try to be helpful by appending catalog, record, etc. metadata
    // onto the return URL. But then I figured, if the integrator (who sent the user to us) wants or
    // needs any such metadata, they can add it to the return URL themselves (in whatever format they expect).
    // So us doing anything at all may just be muddying the water.

    // const recordUrl = `${window.location.origin}/${window.tenancy}/api/v1/catalogs/${catalog}/items/${recordId}`
    // const appStatusUrl = `${recordUrl}/apps/${appName}`
    // parsed.query['knackly-record'] = recordUrl
    // parsed.query['knackly-appstatus'] = appStatusUrl
    return queryString.stringifyUrl(parsed, { parseFragmentIdentifier: true })
  } catch (e) {
    console.error('Invalid return URL: %s', returnUrl)
    console.error(e.message)
    return ''
  }
}
